//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useCallback } from 'react';

interface UseEnterSubmitHandlerProps {
  onSubmit?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  value: string;
  setValue: (value: string) => void;
  submitDisabled?: boolean;
}

export function useEnterSubmitHandler({
  onSubmit,
  value,
  setValue,
  submitDisabled = false
}: UseEnterSubmitHandlerProps) {
  return useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter') return;

    const isModifierSubmit =
      event.shiftKey || event.ctrlKey || event.metaKey || event.altKey;

    // Enter will submit the form.  Shift-Enter, Ctrl-Enter (Cmd-Enter), and
    // Alt-Enter will add new lines.
    if (!isModifierSubmit) {
      event.preventDefault();
      if (!submitDisabled)
        onSubmit?.(event);
    } else if (event.ctrlKey || event.metaKey || event.altKey) {
      // Fix Alt+Enter and Ctrl+Enter (Cmd+Enter) to insert a new line since mui
      // doesn't support these natively.
      const target = event.currentTarget;
      const start = target.selectionStart ?? 0;
      const end = target.selectionEnd ?? 0;

      const newValue =
        value.substring(0, start) + '\n' + value.substring(end);
      setValue(newValue);

      // Set cursor position to the beginning of the new line.
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 1;
      }, 0);
    }
  }, [onSubmit, value, setValue, submitDisabled]);
}
